import React, { Fragment, useState } from "react";
import {
    Box,
    FormControl,
    Typography,
    Paper,
    IconButton,
} from '@mui/material';
import { notificationConstants } from './notificationConstant';
import DatePicker from "react-multi-date-picker";
import DatePanel from "react-multi-date-picker/plugins/date_panel";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { selectMenuProps } from "../../utils/selectMenuProps";
import AppButton from "../Common/Button/Button";
import AppCheckbox from "../Common/FormInputs/AppCheckbox";
import AppRadioGroup from "../Common/FormInputs/AppRadioGroup";
import { useTheme } from "@mui/material/styles";
import { useDesignTokens } from "../../theme/useDesignTokens";

const ScheduleNotification = ({
    setAlert,
    scheduleNotification,
    handleScheduleNotification,
    scheduleType,
    handleScheduleTypeChange,
    multiScheduleDateTime,
    handleMultiScheduleDateTime,
    scheduleDayOfWeek,
    handleScheduleDayOfWeekChange,
    multiScheduleTime,
    handleMultiScheduleTimeChange,
    handleDeleteMultiScheduleTime
}) => {
    const theme = useTheme();
    const { colors, font } = useDesignTokens();
    const primaryColor = colors?.primary?.main || theme.palette.primary.main;

    const [selectedTimeForRepeatScheduling, setSelectedTimeForRepeatScheduling] = useState(undefined);

    const customTextSx = {
        height: '40px',
        paddingLeft: '14px',
        paddingRight: '14px',
        backgroundColor: 'transparent',
        border: '1px solid rgba(0, 0, 0, 0.23)',
        borderRadius: '4px',
        width: '100%',
        boxSizing: 'border-box',
        fontFamily: theme.typography.fontFamily,
        fontSize: '14px',
        outline: 'none',
    };

    function CustomMultipleInput({ onFocus, value, onChange }) {
        return (
            <input
                onFocus={onFocus}
                value={value}
                readOnly
                style={customTextSx}
                onChange={onChange}
            />
        );
    }

    const handleDayToggle = (day) => {
        if (scheduleDayOfWeek.includes(day)) {
            handleScheduleDayOfWeekChange(scheduleDayOfWeek.filter((d) => d !== day));
        } else {
            handleScheduleDayOfWeekChange([...scheduleDayOfWeek, day]);
        }
    };

    function handleAddSelectedTimeForRepeatScheduling() {
        handleMultiScheduleTimeChange(selectedTimeForRepeatScheduling);
    }

    function onDeleteSelectedTimeForRepeatScheduling(index) {
        handleDeleteMultiScheduleTime(index);
    }

    const fieldRowSx = {
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "200px 1fr" },
        alignItems: "center",
        gap: 1.5,
        mb: 2.5,
    };

    const days = [
        { label: "Mon", value: "mon" },
        { label: "Tue", value: "tue" },
        { label: "Wed", value: "wed" },
        { label: "Thu", value: "thu" },
        { label: "Fri", value: "fri" },
        { label: "Sat", value: "sat" },
        { label: "Sun", value: "sun" },
    ];

    return (
        <Fragment>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
                Schedule Notification
            </Typography>

            <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1, mt: 1.5 }}>
                <Box sx={fieldRowSx}>
                    <Typography variant="body2" fontWeight={600}>Schedule Notification</Typography>
                    <Box>
                        <AppCheckbox
                            checked={scheduleNotification}
                            onChange={(e) => handleScheduleNotification(e.target.checked)}
                            color="default"
                            label="Schedule Notification"
                            sx={{ ml: "-11px" }}
                        />
                    </Box>
                </Box>

                {scheduleNotification && (
                    <Fragment>
                        <Box sx={fieldRowSx}>
                            <Typography variant="body2" fontWeight={600}>Select Schedule Type:</Typography>
                            <AppRadioGroup
                                row
                                name="scheduleType"
                                value={scheduleType}
                                onChange={(e) => handleScheduleTypeChange(e.target.value)}
                                options={[
                                    { value: notificationConstants.oneTimeScheduleType, label: "One Time" },
                                    { value: notificationConstants.repeatScheduleType, label: "Repeat" },
                                ]}
                            />
                        </Box>

                        {scheduleType === notificationConstants.oneTimeScheduleType && (
                            <Box sx={fieldRowSx}>
                                <Typography variant="body2" fontWeight={600}>Select Scheduling Date and Time:</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Box sx={{ width: '100%', maxWidth: 280, '.rmdp-container': { width: '100%' } }}>
                                        <DatePicker
                                            minDate={new Date()}
                                            render={<CustomMultipleInput />}
                                            multiple
                                            placeholder="Select Scheduling Dates"
                                            plugins={[
                                                <DatePanel markFocused style={{ width: '100%' }} />,
                                                <TimePicker position="bottom" />,
                                            ]}
                                            format="MM/DD/YYYY HH:mm:ss"
                                            value={multiScheduleDateTime}
                                            onChange={(selectedDateTime) => handleMultiScheduleDateTime(selectedDateTime)}
                                        />
                                    </Box>
                                    <CalendarTodayIcon sx={{ color: 'text.secondary' }} />
                                </Box>
                            </Box>
                        )}

                        {scheduleType === notificationConstants.repeatScheduleType && (
                            <Fragment>
                                <Box sx={fieldRowSx}>
                                    <Typography variant="body2" fontWeight={600}>Select Scheduling Time:</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                                        <Box sx={{ width: '100%', maxWidth: 280, '.rmdp-container': { width: '100%' } }}>
                                            <DatePicker
                                                render={<CustomMultipleInput />}
                                                disableDayPicker
                                                format="hh:mm:ss A"
                                                plugins={[<TimePicker />]}
                                                values={selectedTimeForRepeatScheduling}
                                                onChange={(e) => {
                                                    if (e) setSelectedTimeForRepeatScheduling(new Date(e).getTime());
                                                }}
                                            />
                                        </Box>
                                        <AppButton
                                            variant="primary"
                                            onClick={() => {
                                                if (selectedTimeForRepeatScheduling) {
                                                    handleAddSelectedTimeForRepeatScheduling();
                                                }
                                            }}
                                        >
                                            Add Time
                                        </AppButton>
                                        <CalendarTodayIcon sx={{ color: 'text.secondary' }} />
                                    </Box>
                                </Box>

                                {multiScheduleTime && multiScheduleTime.length > 0 && (
                                    <Box sx={fieldRowSx}>
                                        <Box /> {/* Empty column spacer */}
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {multiScheduleTime.map((scheduledTime, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        bgcolor: 'background.default',
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        borderRadius: 1,
                                                        py: 0.5,
                                                        pl: 1.5,
                                                        pr: 0.5,
                                                        fontSize: 14,
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {new Date(scheduledTime).toLocaleTimeString('en-US', { hour12: false })}
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onDeleteSelectedTimeForRepeatScheduling(index)}
                                                        sx={{ p: 0.25 }}
                                                    >
                                                        <CloseIcon fontSize="small" sx={{ color: 'error.main' }} />
                                                    </IconButton>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}

                                <Box sx={fieldRowSx}>
                                    <Typography variant="body2" fontWeight={600}>Select Days of Week:</Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {days.map((day) => {
                                            const isActive = scheduleDayOfWeek && scheduleDayOfWeek.includes(day.value);
                                            return (
                                                <Box
                                                    key={day.value}
                                                    onClick={() => handleDayToggle(day.value)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        px: 2,
                                                        py: 0.75,
                                                        borderRadius: 1,
                                                        fontSize: 14,
                                                        fontWeight: 600,
                                                        transition: 'all 0.2s',
                                                        border: '1px solid',
                                                        borderColor: isActive ? primaryColor : 'divider',
                                                        bgcolor: isActive ? primaryColor : 'background.paper',
                                                        color: isActive ? '#fff' : 'text.primary',
                                                        '&:hover': {
                                                            bgcolor: isActive ? primaryColor : 'action.hover',
                                                        }
                                                    }}
                                                >
                                                    {day.label}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </Fragment>
                        )}
                    </Fragment>
                )}
            </Paper>
        </Fragment>
    );
};

export default ScheduleNotification;
